import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Library, Package, Puzzle, Settings, Truck, Users } from 'lucide-react'
import { useLayout } from '@/context/layout-provider'
import { ArtemLogoSymbol } from '@/components/artem-logo-symbol'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { TempLogoType } from '@/components/temp-logo-type'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar'
import { NavGroup } from '@/components/layout/nav-group'
import {
  V2_ORGS_ROLES_PATH,
  V2_COMPONENTS_PATH,
  V2_PRODUCTS_PATH,
  V2_SETTINGS_PATH,
  V2_SUPPLIERS_PATH,
} from './utils/routes'

function V2Brand() {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          size="lg"
          className="group-data-[collapsible=icon]:size-10! group-data-[collapsible=icon]:justify-center"
          asChild
        >
          <Link to={V2_PRODUCTS_PATH}>
            <div className="flex aspect-square size-10 shrink-0 items-center justify-center rounded-lg text-sidebar-primary-foreground">
              <ArtemLogoSymbol className="size-10 text-[#02FFD8]" />
            </div>
            <div className="flex flex-col gap-0.5 leading-none group-data-[collapsible=icon]:hidden">
              <TempLogoType className="h-4 shrink-0" />
            </div>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

function V2SidebarFooter() {
  return <ProfileDropdown variant="sidebar" />
}

export function V2Sidebar() {
  const { collapsible, variant } = useLayout()
  const { t } = useTranslation('common')

  const navGroups = useMemo(
    () => [
      {
        title: t('v2.sidebar.group.catalog'),
        items: [
          {
            title: t('v2.nav.products'),
            url: V2_PRODUCTS_PATH,
            icon: Package,
          },
          {
            title: t('v2.nav.components'),
            url: V2_COMPONENTS_PATH,
            icon: Puzzle,
          },
          {
            title: t('v2.nav.suppliers'),
            url: V2_SUPPLIERS_PATH,
            icon: Truck,
          },
        ],
      },
      {
        title: t('v2.sidebar.group.standards'),
        items: [
          {
            title: t('v2.nav.library'),
            url: '/standards-library',
            icon: Library,
            disabled: true,
            comingSoon: {
              badge: t('v2.sidebar.comingSoonBadge'),
              tooltip: t('v2.sidebar.standardsLibraryComingSoon'),
            },
          },
        ],
      },
      {
        title: '',
        items: [
          {
            title: t('v2.nav.organisationsAndRoles'),
            url: V2_ORGS_ROLES_PATH,
            icon: Users,
          },
          {
            title: t('nav.items.settings'),
            url: V2_SETTINGS_PATH,
            icon: Settings,
          },
        ],
      },
    ],
    [t]
  )

  return (
    <Sidebar collapsible={collapsible} variant={variant}>
      <SidebarHeader>
        <V2Brand />
      </SidebarHeader>
      <SidebarContent>
        {navGroups.map((group, index) => (
          <NavGroup key={group.title || `v2-nav-${index}`} {...group} />
        ))}
      </SidebarContent>
      <SidebarFooter>
        <V2SidebarFooter />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
